import React, { useContext, useState, useRef, useEffect } from "react";
import apiService, { handleError } from "../Common/ApiCommon/ApiService";
import icons from "../../assets/images/constantIcons";
import images from "../../assets/images/constantImages";
import LogOut from "../Common/LogOut";
import { UserContext } from "./UserContext";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { setCanteenType } from "../Canteen/Dashboard/canteenTypeSlice";

const Header = ({ sidebarOpen }) => {
  const [isNotificationOpen, setNotificationOpen] = useState(false);
  const [notification, setNotification] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isLogoutOpen, setLogoutOpen] = useState(false);
  const { userData, notificationTrigger } = useContext(UserContext);
  const notificationRef = useRef(null);
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [canPlaySound, setCanPlaySound] = useState(true);
  const seenIdsRef = useRef(new Set());
  const hasMounted = useRef(false);

  const dispatch = useDispatch();
  const canteenType = useSelector((state) => state.canteenType.type)

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const formattedDate = currentDateTime.toLocaleDateString("en-IN", {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour12: true,
  });

  const rawHours = currentDateTime.getHours();
  const hours = (((rawHours + 11) % 12) + 1).toString().padStart(2, "0");
  const ampm = rawHours >= 12 ? "PM" : "AM";
  const minutes = currentDateTime.getMinutes().toString().padStart(2, "0");
  const seconds = currentDateTime.getSeconds().toString().padStart(2, "0");

  const getInitials = (text = "") => {
    const words = text.trim().split(" ");
    const initials = words
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0])
      .join("");
    return initials.toUpperCase();
  };

  const fullImageUrl =
    userData.userType === "vendor" && userData.image
      ? `https://headquater.pixoatic.com${userData.image}`
      : images.defaultprofilepicture;

  const toggleNotification = () => {
    setNotificationOpen(!isNotificationOpen);
  };

  const handleLogout = () => {
    setLogoutOpen(!isLogoutOpen);
  };

  const playNotificationSound = (title) => {
    if (!canPlaySound) return;
    let audioSrc = "/notification.wav";
    if (title === "Upcoming Booking") {
      audioSrc = "/tablenotification.mp3";
    }
    const audio = new Audio(audioSrc);
    audio.play().catch(err => {
      console.warn("Audio play blocked:", err);
    });
  };

  const showToast = (title) => {
    playNotificationSound(title);
    toast(" You got a new notification!", {
      icon: "📢",
      duration: 1000,
      style: {
        background: "#13A63B",
        color: "#fff",
        fontWeight: "500",
        borderRadius: "8px",
      },
    });
  };

  const fetchAllNotification = async () => {
    try {
      const response = await apiService.get(`/notifications`);
      const newNotifications = response.data;

      const unseen = newNotifications.filter(
        (n) => !seenIdsRef.current.has(n.id)
      );

      if (hasMounted.current && unseen.length > 0) {
        unseen?.forEach(n => {
          showToast(n.title);
        });
      }

      newNotifications?.forEach((n) => seenIdsRef.current.add(n.id));

      setNotification(newNotifications);
      hasMounted.current = true;
    } catch (errors) {
      handleError(errors);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userData?.userType === "vendor") {
      setLoading(true);
      fetchAllNotification();
    }
  }, [notificationTrigger]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setNotificationOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <>
      <div className="h-[12vh] flex justify-between items-center px-4 md:px-7 py-7">
        <button onClick={sidebarOpen} className="block lg:hidden ">
          <icons.CgMenuLeftAlt className="text-G80 text-2xl" />
        </button>

        <div className="flex items-center gap-4">

          <h1 className="text-base md:text-xl lg:text-2xl text-green700 font-bold">
            {userData.store_name || ""}
          </h1>
          {userData.userType === "canteenUser" && (
            <div className="flex border border-green rounded-2xl overflow-hidden w-fit">
              {["prepaid", "postpaid"].map((type) => (
                <button
                  key={type}
                  onClick={() => dispatch(setCanteenType(type))}
                  className={`px-2 md:px-5 py-2 text-sm font-medium capitalize transition-all duration-200
            ${canteenType === type ? "bg-green text-white" : "bg-white text-green"}
          `}
                >
                  {type}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Right: Notification, Time, Profile, Logout */}
        <div className="flex items-center space-x-4 md:space-x-8 ml-auto relative">
          <div className="hidden md:flex flex-col justify-center mr-4">
            <p className="text-left text-sm text-Grey700 font-semibold mb-1">
              {formattedDate}
            </p>
            <div className="flex gap-1 items-center text-sm font-semibold">
              <div className="px-2 py-1 border bg-green rounded-md shadow text-white w-9">
                {hours}
              </div>
              <span className="text-base font-bold">:</span>
              <div className="px-2 py-1 border bg-green rounded-md shadow text-white w-9">
                {minutes}
              </div>
              <span className="text-base font-bold">:</span>
              <div className="px-2 py-1 border bg-green rounded-md shadow text-white w-9">
                {seconds}
              </div>
              <span className="ml-1 text-green">{ampm}</span>
            </div>
          </div>

          {userData.userType === "vendor" && (
            <>
              <div
                className="w-[38px] h-[38px] s:w-[48px] s:h-[48px] rounded-full bg-green50 flex justify-center items-center cursor-pointer"
                onClick={toggleNotification}
              >
                <img
                  src={images.notificationbell}
                  alt="notificationbell"
                  className="object-cover"
                />
              </div>

              {isNotificationOpen && (
                <div
                  ref={notificationRef}
                  className="w-[260px] md:w-[497px] absolute top-12 sx:right-6 s:right-14 md:right-40 bg-white rounded-xl shadow-lg ring-1 ring-black ring-opacity-5 z-30 p-2 md:p-4"
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-semibold text-[16px] text-G500">
                      Notifications
                    </span>
                    <div
                      className="bg-green50 rounded-full p-2 cursor-pointer w-[32px] h-[32px]"
                      onClick={toggleNotification}
                    >
                      <icons.IoClose className=" text-green700 " />
                    </div>
                  </div>
                  <div className="h-[200px] overflow-y-auto vertical">
                    {notification.length > 0 ? (
                      notification.slice(0, 4).map((notify, index) => (
                        <div
                          key={index}
                          className={`flex justify-between items-center py-2 border-t border-G20`}
                        >
                          <div className="w-[80%] flex items-center gap-3 pr-1">
                            <div className="w-[36px] h-[36px] rounded-full bg-green50 flex items-center justify-center">
                              {notify.icon ? (
                                <img
                                  src={notify.icon}
                                  alt="icon"
                                  className="w-[20px] h-[20px]"
                                />
                              ) : (
                                <span className="text-xs font-semibold text-green700">
                                  {getInitials(notify.title)}
                                </span>
                              )}
                            </div>
                            <div>
                              <h4 className="text-sm font-normal text-G800">
                                {notify.title}
                              </h4>
                              <p className="text-G80 font-normal text-[12px]">
                                {notify.body}
                              </p>
                            </div>
                          </div>
                          <div className="flex flex-wrap justify-end w-[20%]">
                            <span className="text-G80 font-medium text-[10px]">
                              {notify.time}
                            </span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center text-sm text-G500">
                        No new notifications
                      </div>
                    )}
                  </div>
                  <Link
                    to="/mainDashboard/viewAllNotification"
                    onClick={() => setNotificationOpen(false)}
                    className="border-t border-G20 flex justify-center"
                  >
                    <button className="inline-block px-5 py-2 bg-buttoncolor text-white rounded-lg cardtext mt-4">
                      View all notifications
                    </button>
                  </Link>
                </div>
              )}
            </>
          )}

          <Link to="/mainDashboard/setting">
            <div className="flex flex-col lg:flex-row gap-1 md:gap-3 justify-center items-center">
              <img
                src={fullImageUrl}
                alt="Profile"
                loading="lazy"
                className="object-cover w-[38px] h-[38px] s:w-[48px] s:h-[48px] rounded-full bg-slate-300"
              />
              <div className="flex flex-col sx:hidden xl:block">
                <h1 className="text-[10px] md:text-[14px] font-medium text-Grey700">
                  {userData.userName}
                </h1>
                <p className="text-Grey100 text-[8px] md:text-[12px] font-normal">
                  {userData.userType}
                </p>
              </div>
            </div>
          </Link>

          <div
            onClick={handleLogout}
            className="w-[38px] h-[38px] s:w-[48px] s:h-[48px] rounded-full bg-green50 flex justify-center items-center cursor-pointer"
          >
            <img src={images.logout} alt="logout" />
          </div>
        </div>
        <LogOut isOpen={isLogoutOpen} onClose={handleLogout} />
      </div>
    </>
  );
};

export default Header;
